import { Cubit } from "blac-next";
import { fileState, toast } from "src/state/state";
import reportErrorSentry from "src/lib/reportErrorSentry";
import {
  AddMealInsightFeedbackRequest,
  CreateMealInsightResponse,
  MealInsightControllerService
} from "@9amhealth/openapi";
import FileType from "src/constants/fileType";
import { createAppPath } from "src/lib/createAppPath";
import Path from "src/constants/path";

interface MealInsightState {
  loading: boolean;
  fileId?: string;
  mealInsight?: CreateMealInsightResponse;
  error?: Error;
  imageError?: Error;
}

class MealInsightBloc extends Cubit<MealInsightState> {
  static keepAlive = true;

  constructor() {
    super({
      loading: false
    });
  }

  uploadPhoto = async (file: File): Promise<string | undefined> => {
    this.patch({ loading: true, imageError: undefined });

    try {
      const upload = await fileState.uploadFile({
        file,
        fileAttributes: {
          "file.type": FileType.MEAL_INSIGHTS
        }
      });

      if (!upload) {
        throw new Error("Failed to upload file");
      }

      this.patch({
        loading: false,
        fileId: upload.id
      });
      return upload.id;
    } catch (error: unknown) {
      this.patch({ imageError: error as Error });
      reportErrorSentry(error);
    } finally {
      this.patch({ loading: false });
    }
  };

  handleUploadPhoto = async (
    file: File,
    navigate: (path: string, options?: { replace?: boolean }) => void
  ): Promise<void> => {
    const tempPath = `${createAppPath(Path.mealInsights)}/${Path.tmp}`;
    navigate(tempPath);

    const fileId = await this.uploadPhoto(file);

    if (fileId) {
      const targetUrl = `${createAppPath(Path.mealInsights)}/${fileId}`;
      navigate(targetUrl, {
        replace: true
      });
    }
  };

  clearMealInsight = () => {
    this.patch({
      loading: false,
      mealInsight: undefined,
      error: undefined
    });
  };

  generateMealInsight = async (fileId: string) => {
    this.clearMealInsight();

    try {
      this.patch({ loading: true });
      const response = await MealInsightControllerService.createMealInsight({
        fileId
      });

      this.patch({
        loading: false,
        mealInsight: response.data
      });
    } catch (error: unknown) {
      toast.error("mealInsights.error");
      this.patch({ loading: false, error: error as Error });
      reportErrorSentry(error);
    }
  };

  rateMealInsight = async (rating: AddMealInsightFeedbackRequest.rating) => {
    const mealInsightId = this.state.mealInsight?.id;
    if (!mealInsightId) {
      throw new Error("Meal insight ID is required");
    }

    try {
      await MealInsightControllerService.addMealInsightFeedback(mealInsightId, {
        rating
      });
    } catch (error: unknown) {
      reportErrorSentry(error);
    }
  };
}

export default MealInsightBloc;
